if (!customElements.get('ovani-reviews-marquee')) {
  class OvaniReviewsMarquee extends HTMLElement {
    connectedCallback() {
      this.cleanup();
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.speed = Number(this.dataset.speed || 28);
      this.states = [];
      this.abort = new AbortController();
      this.onVisibility = this.onVisibility.bind(this);
      this.tick = this.tick.bind(this);
      this.querySelectorAll('[data-reviews-row]').forEach((row) => this.setupRow(row));
      document.addEventListener('visibilitychange', this.onVisibility);
      this.lastTime = 0;
      this.raf = requestAnimationFrame(this.tick);
      this.loadJudgeMeReviews();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    cleanup() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
      if (this.abort) this.abort.abort();
      if (this.onVisibility) {
        document.removeEventListener('visibilitychange', this.onVisibility);
      }
      (this.states || []).forEach((state) => {
        if (state.resizeObserver) state.resizeObserver.disconnect();
      });
      this.states = [];
    }

    setupRow(row) {
      const track = row.querySelector('[data-reviews-track]');
      const original = row.querySelector('[data-reviews-set]');
      if (!track || !original) return;

      track.querySelectorAll('[data-reviews-set]').forEach((set, index) => {
        if (index > 0) set.remove();
      });

      const state = {
        row,
        track,
        original,
        offset: 0,
        dir: row.getAttribute('data-direction') === 'right' ? 1 : -1,
        paused: this.reduceMotion,
        dragging: false,
        setWidth: 0,
        lastX: 0,
      };

      requestAnimationFrame(() => {
        this.fillClones(state);
        this.measure(state);
        this.apply(state);
      });

      state.resizeObserver = new ResizeObserver(() => {
        this.fillClones(state);
        this.measure(state);
        this.wrapOffset(state);
        this.apply(state);
      });
      state.resizeObserver.observe(row);

      row.setAttribute('tabindex', '0');
      const signal = this.abort.signal;

      row.addEventListener(
        'pointerdown',
        (event) => {
          if (event.button && event.button !== 0) return;
          state.dragging = true;
          state.paused = true;
          state.lastX = event.clientX;
          row.setPointerCapture(event.pointerId);
          row.classList.add('is-dragging');
        },
        { signal }
      );

      row.addEventListener(
        'pointermove',
        (event) => {
          if (!state.dragging) return;
          state.offset += event.clientX - state.lastX;
          state.lastX = event.clientX;
          this.wrapOffset(state);
          this.apply(state);
        },
        { signal }
      );

      const endDrag = () => {
        if (!state.dragging) return;
        state.dragging = false;
        row.classList.remove('is-dragging');
        if (document.activeElement === row) row.blur();
        const hoverCapable = window.matchMedia('(hover: hover)').matches;
        const stillHovering = hoverCapable && row.matches(':hover');
        state.paused = this.reduceMotion || stillHovering;
      };

      row.addEventListener('pointerup', endDrag, { signal });
      row.addEventListener('pointercancel', endDrag, { signal });
      row.addEventListener(
        'mouseenter',
        () => {
          if (!state.dragging) state.paused = true;
        },
        { signal }
      );
      row.addEventListener(
        'mouseleave',
        () => {
          if (!state.dragging && !row.contains(document.activeElement) && !this.reduceMotion) {
            state.paused = false;
          }
        },
        { signal }
      );
      row.addEventListener(
        'focusin',
        () => {
          state.paused = true;
        },
        { signal }
      );
      row.addEventListener(
        'focusout',
        (event) => {
          if (row.contains(event.relatedTarget) || state.dragging || this.reduceMotion) return;
          if (window.matchMedia('(hover: hover)').matches && row.matches(':hover')) return;
          state.paused = false;
        },
        { signal }
      );

      this.states.push(state);
      this.apply(state);
    }

    async loadJudgeMeReviews() {
      if (this.dataset.judgeme !== 'true') return;
      const origin = String(this.dataset.workerUrl || '')
        .trim()
        .replace(/\/+$/, '');
      if (!origin) return;
      const limit = Math.max(1, Math.min(50, Number(this.dataset.judgemeLimit || 24) || 24));
      try {
        const response = await fetch(origin + '/reviews/marquee?limit=' + encodeURIComponent(String(limit)), {
          signal: this.abort.signal,
        });
        if (!response.ok) return;
        const payload = await response.json();
        const reviews = Array.isArray(payload && payload.reviews) ? payload.reviews : [];
        this.appendJudgeMeReviews(reviews);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
      }
    }

    appendJudgeMeReviews(reviews) {
      const rows = this.ensureRows();
      if (!rows.length) return;
      const existing = new Set();
      this.querySelectorAll('[data-reviews-set]:not([aria-hidden]) [data-review-card]').forEach((card) => {
        const key = marqueeQuoteKey(card.querySelector('.ovani-reviews-marquee__quote')?.textContent);
        if (key) existing.add(key);
      });

      let added = 0;
      reviews.forEach((review) => {
        const quote = String((review && review.quote) || '').trim();
        const key = marqueeQuoteKey(quote);
        if (!key || existing.has(key)) return;
        existing.add(key);
        const set = this.shorterRow(rows).querySelector('[data-reviews-set]');
        if (!set) return;
        set.appendChild(
          this.createReviewCard({
            quote,
            author: String((review && review.author) || '').trim(),
          })
        );
        added += 1;
      });
      if (added) this.rebuildTracks();
    }

    ensureRows() {
      const rows = Array.from(this.querySelectorAll('[data-reviews-row]'));
      if (rows.length >= 2) return rows;
      if (rows.length === 0) {
        const left = this.createRow('left');
        const right = this.createRow('right');
        this.setupRow(left);
        this.setupRow(right);
        return [left, right];
      }
      const extraDir = rows[0].getAttribute('data-direction') === 'right' ? 'left' : 'right';
      this.setupRow(this.createRow(extraDir));
      return Array.from(this.querySelectorAll('[data-reviews-row]'));
    }

    createRow(direction) {
      const row = document.createElement('div');
      row.className = 'ovani-reviews-marquee__row';
      row.setAttribute('data-reviews-row', '');
      row.setAttribute('data-direction', direction);
      const track = document.createElement('div');
      track.className = 'ovani-reviews-marquee__track';
      track.setAttribute('data-reviews-track', '');
      const set = document.createElement('div');
      set.className = 'ovani-reviews-marquee__set';
      set.setAttribute('data-reviews-set', '');
      track.appendChild(set);
      row.appendChild(track);
      this.appendChild(row);
      return row;
    }

    createReviewCard(review) {
      const figure = document.createElement('figure');
      figure.className = 'ovani-reviews-marquee__card';
      figure.setAttribute('data-review-card', '');

      const mark = document.createElement('span');
      mark.className = 'ovani-reviews-marquee__mark';
      mark.setAttribute('aria-hidden', 'true');
      mark.textContent = '“';

      const wrap = document.createElement('div');
      wrap.className = 'ovani-reviews-marquee__quote-wrap';
      const quote = document.createElement('blockquote');
      quote.className = 'ovani-reviews-marquee__quote';
      quote.textContent = review.quote;
      wrap.appendChild(quote);

      figure.appendChild(mark);
      figure.appendChild(wrap);

      if (review.author) {
        const author = document.createElement('figcaption');
        author.className = 'ovani-reviews-marquee__author';
        author.textContent = review.author;
        figure.appendChild(author);
      }
      return figure;
    }

    shorterRow(rows) {
      let best = rows[0];
      let bestCount = this.rowCardCount(best);
      for (let i = 1; i < rows.length; i += 1) {
        const count = this.rowCardCount(rows[i]);
        if (count < bestCount) {
          best = rows[i];
          bestCount = count;
        }
      }
      return best;
    }

    rowCardCount(row) {
      const set = row.querySelector('[data-reviews-set]');
      return set ? set.querySelectorAll('[data-review-card]').length : 0;
    }

    rebuildTracks() {
      (this.states || []).forEach((state) => {
        state.track.querySelectorAll('[data-reviews-set]').forEach((set, index) => {
          if (index > 0) set.remove();
        });
        this.fillClones(state);
        this.measure(state);
        this.wrapOffset(state);
        this.apply(state);
      });
    }

    fillClones(state) {
      if (!state.original.offsetWidth) return;
      const rowWidth = state.row.offsetWidth || 1;
      let guard = 0;
      const appendClone = () => {
        const clone = state.original.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        clone.querySelectorAll('[data-shopify-editor-block]').forEach((node) => {
          node.removeAttribute('data-shopify-editor-block');
        });
        state.track.appendChild(clone);
      };
      if (state.track.querySelectorAll('[data-reviews-set]').length < 2) {
        appendClone();
        guard += 1;
      }
      while (state.track.scrollWidth < rowWidth * 2 + 40 && guard < 8) {
        appendClone();
        guard += 1;
      }
    }

    measure(state) {
      const gap = Number.parseFloat(getComputedStyle(state.track).gap) || 0;
      state.setWidth = state.original.offsetWidth + gap;
    }

    wrapOffset(state) {
      const width = state.setWidth;
      if (!width) return;
      while (state.offset <= -width) state.offset += width;
      while (state.offset > 0) state.offset -= width;
    }

    apply(state) {
      state.track.style.transform = 'translate3d(' + state.offset + 'px,0,0)';
    }

    tick(now) {
      const last = this.lastTime || now;
      const dt = Math.min(0.05, (now - last) / 1000);
      this.lastTime = now;
      const hidden = document.hidden;
      this.states.forEach((state) => {
        if (!hidden && !state.paused && !state.dragging && !this.reduceMotion) {
          state.offset += state.dir * this.speed * dt;
          this.wrapOffset(state);
          this.apply(state);
        }
      });
      this.raf = requestAnimationFrame(this.tick);
    }

    onVisibility() {
      if (!document.hidden) this.lastTime = 0;
    }
  }

  function marqueeQuoteKey(raw) {
    return String(raw || '')
      .toLowerCase()
      .replace(/[“”"'‘’]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  customElements.define('ovani-reviews-marquee', OvaniReviewsMarquee);
}
